import { createSlice } from '@reduxjs/toolkit'
import _ from 'lodash'

import { makeErrorMessage, UNREACHABLE_ERROR_STATUS_CODE, UNAUTHORIZED_ERROR_STATUS_CODE } from 'api/utils'
import * as API from 'api/workers'

import * as NetworkErrorDialog from 'slices/networkErrorDialogSlice'
import { validateToken } from 'slices/sessionSlice'
import * as SessionTimeoutDialog from 'slices/sessionTimeoutDialogSlice'
import * as Spinner from 'slices/spinnerSlice'
import { commonParams } from 'slices/utils'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import type { AppThunk, RootState } from 'store'

export type CreateUpdateParam = {
  createData: API.WorkerScheduleUpdateType[]
  updateData: Array<{ data: API.WorkerScheduleUpdateType; scheduleId: number }>
}

type WorkersState = {
  isRequesting: boolean
  errorMessage: string
  partialWorkers: API.PartialWorkerData[]
  worker?: API.WorkerData
  downloadUrl: string | undefined
}

const initialState: WorkersState = {
  isRequesting: false,
  errorMessage: '',
  partialWorkers: [],
  worker: undefined,
  downloadUrl: undefined,
}

export const workersSlice = createSlice({
  name: 'workers',
  initialState,
  reducers: {
    startRequest: state => {
      state.isRequesting = true
      state.errorMessage = ''
    },
    apiFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isRequesting = false
      state.errorMessage = action.payload.errorMessage
    },
    getWorkerListSuccess: (state, action: PayloadAction<API.WorkerListResponse>) => {
      state.isRequesting = false
      state.partialWorkers = _.sortBy(action.payload.partialWorkers, 'wmsMemberId')
    },
    getWorkerSuccess: (state, action: PayloadAction<API.WorkerResponse>) => {
      state.isRequesting = false
      state.worker = action.payload.worker
    },

    requestSuccess: state => {
      state.isRequesting = false
    },
    getExportDataUrlSuccess: (state, action: PayloadAction<API.CheckExportDataReadyResponse>) => {
      state.isRequesting = false
      state.downloadUrl = action.payload.downloadUrl
    },
    resetExportDataUrl: state => (state.downloadUrl = undefined),
  },
})

export const {
  startRequest,
  apiFailure,
  getWorkerListSuccess,
  getWorkerSuccess,
  requestSuccess,
  getExportDataUrlSuccess,
  resetExportDataUrl,
} = workersSlice.actions

// memo tenantSliceに同じAPIが存在するため、このメソッドを変更して利用できるようにする
// 変更点 tenantIdを追加し、別テナントのワーカーを取得できるようにする
export const getWorkerList =
  (tenantId?: number): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())

    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    const params = tenantId ? _.merge(commonParams(getState), { tenantId }) : commonParams(getState)

    API.getWorkerList(params)
      .then((res: API.WorkerListResponse) => dispatch(getWorkerListSuccess(res)))
      .catch((res: AxiosError) => {
        const errorCode = makeErrorMessage(res)
        if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
          dispatch(SessionTimeoutDialog.open())
        } else {
          dispatch(NetworkErrorDialog.open({ code: errorCode }))
        }
        dispatch(apiFailure({ errorMessage: errorCode }))
      })
      .finally(() => dispatch(Spinner.stop()))
  }

export const getWorker =
  (workerId: number): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())

    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    API.getWorker(commonParams(getState), workerId)
      .then((res: API.WorkerResponse) => dispatch(getWorkerSuccess(res)))
      .catch((res: AxiosError) => {
        const errorCode = makeErrorMessage(res)
        if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
          dispatch(SessionTimeoutDialog.open())
        } else {
          dispatch(NetworkErrorDialog.open({ code: errorCode }))
        }
        dispatch(apiFailure({ errorMessage: errorCode }))
      })
      .finally(() => dispatch(Spinner.stop()))
  }

export const createWorker =
  (data: API.CreateWorkerEditDataType): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())
    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    API.createWorker(commonParams(getState), data)
      .then(res => {
        dispatch(requestSuccess())
        dispatch(getWorkerList())
        dispatch(getWorker(res.id))
      })
      .catch((res: AxiosError) => {
        const errorCode = makeErrorMessage(res)
        if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
          dispatch(SessionTimeoutDialog.open())
        } else if (errorCode === UNREACHABLE_ERROR_STATUS_CODE) {
          dispatch(NetworkErrorDialog.open({ code: errorCode }))
        }
        dispatch(apiFailure({ errorMessage: errorCode }))
      })
      .finally(() => dispatch(Spinner.stop()))
  }

export const updateWorker =
  (workerId: number, data: API.UpdateWorkerEditDataType): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())
    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    API.updateWorker(commonParams(getState), workerId, data)
      .then(() => {
        dispatch(requestSuccess())
        dispatch(getWorkerList())
        dispatch(getWorker(workerId))
      })
      .catch((res: AxiosError) => {
        const errorCode = makeErrorMessage(res)
        if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
          dispatch(SessionTimeoutDialog.open())
        } else if (errorCode === UNREACHABLE_ERROR_STATUS_CODE) {
          dispatch(NetworkErrorDialog.open({ code: errorCode }))
        }
        dispatch(apiFailure({ errorMessage: errorCode }))
      })
      .finally(() => dispatch(Spinner.stop()))
  }

export const deleteWorker =
  (workerId: number): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())

    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    API.deleteWorker(commonParams(getState), workerId)
      .then(() => {
        dispatch(requestSuccess())
        dispatch(getWorkerList())
      })
      .catch((res: AxiosError) => {
        const errorCode = makeErrorMessage(res)
        if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
          dispatch(SessionTimeoutDialog.open())
        } else if (errorCode === UNREACHABLE_ERROR_STATUS_CODE) {
          dispatch(NetworkErrorDialog.open({ code: errorCode }))
        }
        dispatch(apiFailure({ errorMessage: errorCode }))
      })
      .finally(() => dispatch(Spinner.stop()))
  }

export const importWorkers =
  (fileName: string, csvContent: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())
    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    try {
      const response = await API.workersUploadUrl(commonParams(getState), fileName)
      await API.putUploadUrl(response.uploadUrl, csvContent)
      dispatch(requestSuccess())
    } catch (error) {
      const errorCode = makeErrorMessage(error as AxiosError)
      if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
        dispatch(SessionTimeoutDialog.open())
      } else {
        dispatch(NetworkErrorDialog.open({ code: errorCode }))
      }
      dispatch(apiFailure({ errorMessage: errorCode }))
    } finally {
      dispatch(Spinner.stop())
    }
  }

export const exportWorkers =
  (targetWorkspaceIds: number[], replaceAverageProductivity: boolean, includeUnaffiliatedWorker: boolean): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startRequest())
    const valid = await dispatch(validateToken())
    if (!valid) {
      return
    }

    dispatch(Spinner.start())
    try {
      const exportDataResponse = await API.exportWorkers(
        commonParams(getState),
        targetWorkspaceIds,
        replaceAverageProductivity,
        includeUnaffiliatedWorker
      )
      if (exportDataResponse?.downloadUrl) {
        return dispatch(getExportDataUrlSuccess(exportDataResponse))
      }
    } catch (err) {
      const errorCode = makeErrorMessage(err as AxiosError)
      if (errorCode === UNAUTHORIZED_ERROR_STATUS_CODE) {
        dispatch(SessionTimeoutDialog.open())
      } else {
        dispatch(NetworkErrorDialog.open({ code: errorCode }))
      }
      dispatch(apiFailure({ errorMessage: errorCode }))
    } finally {
      dispatch(Spinner.stop())
    }
  }

export const selectWorkersStatus = (state: RootState) => ({ ...state.workers })

export default workersSlice.reducer
